/**
 * ZoneExitLink — a link OUT of the tracked acquisition journey INTO the
 * signed-in app (`/patients`, `/clinicians`, `/admin`).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: tracking A1.
 *
 * WHY it is NOT a react-router `<Link>`: `isTrackedPath` only gates whether the
 * GTM container is ever LOADED — it cannot UNLOAD one already injected. A
 * client-side `<Link>` keeps the same document, so the container loaded on the
 * journey (e.g. `/weightloss → checkout → order confirmation`) would ride the
 * SPA transition straight into `/patients` and send signed-in page paths to
 * GA4 — tying a consented client id to *being a patient of a weight-loss
 * clinic* (a Part F category leak). Rendering a PLAIN anchor forces a full
 * DOCUMENT navigation, so the untracked zone always starts a fresh, GTM-free
 * document. Use this at EVERY journey→app entry point; never a `<Link>`.
 *
 * (Defence in depth: the GTM container's GA4 tag should also carry a trigger
 * exception on `page_path ^/(patients|clinicians|admin|auth)` — that config
 * lives outside this repo, so this client-side hard line is the one we own.)
 *
 * BASENAME-AWARE (Clinics-in-Core, 2026-09-07). Because this is a raw anchor,
 * react-router's `basename` cannot rewrite it the way it rewrites a `<Link>`.
 * `to` is therefore an app-ROOT path ('/patients') and the prefix the build is
 * served under is added here — '' at the origin root, '/clinics' when GPS Core
 * fronts the portal — so the same call sites work in both deployments and stay
 * a full document load in both. `withBasename` is a no-op for standalone builds.
 */
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { withBasename } from '../config';

export function ZoneExitLink({
  to,
  children,
  ...rest
}: { to: string; children: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  // A real anchor (no client-side interception) → full document load.
  return (
    <a href={withBasename(to)} {...rest}>
      {children}
    </a>
  );
}
